import React, { useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertCircle,
  AlertOctagon,
  AlertTriangle,
  Bell,
  Camera,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Gauge,
  HardHat,
  RefreshCw,
  Send,
  Shield,
  ShieldAlert,
  Thermometer,
  Timer,
  TrendingDown,
  TrendingUp,
  Users,
  Video,
} from "lucide-react";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { Modal, Empty, message } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import axios from "../../utils/axiosDash";
import { eventEmitter } from "../../utils/eventEmitter";
import CampusLiveView from "./campusliveview";

dayjs.extend(relativeTime);

// ── HELPERS ─────────────────────────────────────────────────────────────
const getTimeFromIso = (isoStr) => {
  if (!isoStr) return "—";
  const value =
    typeof isoStr === "object" ? isoStr?.toISOString?.() || "" : String(isoStr);
  const time = value.split("T")[1];
  return time ? time.substring(0, 8) : "—";
};

const SEVERITY_STYLES = {
  critical: { badge: "border-rose-200 bg-rose-50 text-rose-600", dot: "bg-rose-500", icon: AlertOctagon },
  high: { badge: "border-amber-200 bg-amber-50 text-amber-600", dot: "bg-amber-500", icon: AlertTriangle },
  medium: { badge: "border-sky-200 bg-sky-50 text-sky-600", dot: "bg-sky-500", icon: AlertCircle },
  low: { badge: "border-emerald-200 bg-emerald-50 text-emerald-600", dot: "bg-emerald-500", icon: CheckCircle2 },
};

const severityStyle = (severity) => SEVERITY_STYLES[severity] || SEVERITY_STYLES.low;

// ── KPI TILE (fixed-width-safe: labels wrap instead of truncating) ──────
const TONE_STYLES = {
  red: { bg: "bg-rose-50", text: "text-rose-600" },
  amber: { bg: "bg-amber-50", text: "text-amber-600" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-600" },
  violet: { bg: "bg-violet-50", text: "text-violet-600" },
  blue: { bg: "bg-sky-50", text: "text-sky-600" },
  teal: { bg: "bg-teal-50", text: "text-teal-600" },
};

const KpiTile = ({ label, value, icon: Icon, tone, trend }) => {
  const t = TONE_STYLES[tone] || TONE_STYLES.blue;

  return (
    <div className="flex items-start gap-3 rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${t.bg}`}>
        <Icon size={17} className={t.text} strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[12px] font-medium leading-snug text-slate-500">{label}</p>
        <div className="mt-0.5 flex items-baseline gap-1.5">
          <p className="text-xl font-semibold leading-tight text-slate-900">{value}</p>
          {trend && (
            <span
              className={`flex items-center gap-0.5 whitespace-nowrap text-[11px] font-medium ${
                trend.good ? "text-emerald-600" : "text-rose-600"
              }`}
            >
              {trend.good ? <TrendingDown size={11} /> : <TrendingUp size={11} />}
              {trend.label}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ── PANEL SHELL (keeps the three main columns visually aligned) ─────────
const Panel = ({ title, subtitle, icon: Icon, right, children, bodyClassName = "", fixedHeight = true }) => (
  <div
    className={`flex h-auto flex-col overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${
      fixedHeight ? "lg:h-[620px]" : ""
    }`}
  >
    <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
      <div className="flex items-center gap-2.5">
        {Icon && (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-500">
            <Icon size={15} />
          </span>
        )}
        <div className="min-w-0">
          <h2 className="text-[13.5px] font-semibold text-slate-900">{title}</h2>
          {subtitle && <p className="truncate text-[11.5px] text-slate-400">{subtitle}</p>}
        </div>
      </div>
      {right}
    </div>
    <div className={`flex-1 overflow-y-auto ${bodyClassName}`}>{children}</div>
  </div>
);

// ── STATUS ROW (reception / security presence) ───────────────────────────
const StatusRow = ({ title, data, Icon }) => {
  const isPresent = data?.status === true;
  // const label = data ? (isPresent ? "Present" : "Absent") : "No data";
  const dot = data ? (isPresent ? "bg-emerald-500" : "bg-rose-500") : "bg-slate-300";
  const textColor = data ? (isPresent ? "text-emerald-600" : "text-rose-600") : "text-slate-400";
  const cameraName = data?.camera_name ? data.camera_name.replace(/_/g, " ") : "Camera assigned";

  return (
    <div className="flex items-center justify-between gap-3 px-5 py-3.5">
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-50">
          <Icon size={15} className="text-slate-500" />
        </div>
        <div className="min-w-0">
          <p className="text-[13px] font-medium text-slate-800">{title}</p>
          <p className="truncate text-[11.5px] text-slate-400">{cameraName}</p>
        </div>
      </div>
      {/* <div className="flex shrink-0 items-center gap-1.5 rounded-full bg-slate-50 px-2.5 py-1">
        <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
        <span className={`text-[11.5px] font-semibold ${textColor}`}>{label}</span>
      </div> */}
    </div>
  );
};

// ── CAMERA DETECTION HEATMAP (ported from the analytics dashboard) ──────
const HEAT_STEPS = [
  { t: 0.2, bg: "#EDE9FE", text: "#1E1B2E" },
  { t: 0.4, bg: "#C4B5FD", text: "#1E1B2E" },
  { t: 0.6, bg: "#8B5CF6", text: "#FFFFFF" },
  { t: 0.8, bg: "#6D28D9", text: "#FFFFFF" },
  { t: 1.001, bg: "#4C1D95", text: "#FFFFFF" },
];

const heatColorFor = (val, max) => {
  if (!val) return { bg: "#F8FAFC", text: "#94A3B8" };
  const t = val / max;
  return HEAT_STEPS.find((s) => t <= s.t) || HEAT_STEPS[HEAT_STEPS.length - 1];
};

const CameraDetectionHeatmap = ({ events }) => {
  const heatmap = useMemo(() => {
    if (!events?.length) return { cameras: [], hours: [], grid: {}, max: 1 };
    const cameras = [...new Set(events.map((e) => e.camera_id || "Unknown"))].sort();
    const hoursSet = new Set();
    const grid = {};

    events.forEach((e) => {
      const hour = e.timestamp ? dayjs(e.timestamp).format("HH:00") : null;
      if (!hour) return;
      hoursSet.add(hour);
      const key = `${e.camera_id || "Unknown"}__${hour}`;
      grid[key] = (grid[key] || 0) + 1;
    });

    const hours = [...hoursSet].sort();
    const max = Math.max(...Object.values(grid), 1);
    return { cameras, hours, grid, max };
  }, [events]);

  if (!heatmap.cameras.length) {
    return (
      <div className="py-10">
        <Empty description="No detection activity yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[560px] border-separate border-spacing-1">
        <thead>
          <tr>
            <th className="w-[110px] px-2 py-1 text-left text-[10px] font-semibold uppercase tracking-[0.06em] text-slate-400">
              Camera
            </th>
            {heatmap.hours.map((h) => (
              <th key={h} className="min-w-[36px] px-0.5 py-1 text-center text-[10px] font-medium text-slate-400">
                {h}
              </th>
            ))}
            <th className="px-2 py-1 text-center text-[10px] font-semibold uppercase tracking-[0.06em] text-slate-400">
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {heatmap.cameras.map((cam) => {
            const rowTotal = heatmap.hours.reduce((s, h) => s + (heatmap.grid[`${cam}__${h}`] || 0), 0);
            return (
              <tr key={cam}>
                <td className="whitespace-nowrap px-2 py-0.5 text-[11.5px] font-semibold text-slate-700">
                  <span className="flex items-center gap-1.5">
                    <Camera size={10} className="text-violet-500" />
                    {cam}
                  </span>
                </td>
                {heatmap.hours.map((h) => {
                  const val = heatmap.grid[`${cam}__${h}`] || 0;
                  const c = heatColorFor(val, heatmap.max);
                  return (
                    <td key={h} className="p-0.5">
                      <div
                        title={`${cam} at ${h} — ${val} detections`}
                        className="flex h-7 items-center justify-center rounded-md text-[10px] font-semibold transition-transform hover:scale-110"
                        style={{ background: c.bg, color: c.text }}
                      >
                        {val || ""}
                      </div>
                    </td>
                  );
                })}
                <td className="text-center text-[12px] font-bold text-violet-600">{rowTotal}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-medium text-slate-500">Intensity</span>
        {[
          { label: "None", bg: "#F8FAFC" },
          { label: "Low", bg: "#EDE9FE" },
          { label: "Mid", bg: "#C4B5FD" },
          { label: "High", bg: "#8B5CF6" },
          { label: "Peak", bg: "#4C1D95" },
        ].map((l) => (
          <span key={l.label} className="flex items-center gap-1.5">
            <span className="h-3.5 w-5 rounded border border-slate-200" style={{ background: l.bg }} />
            <span className="text-[10.5px] text-slate-400">{l.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

// ── MAIN COMPONENT ─────────────────────────────────────────────────────
const CommandDashboard = () => {
  const [alerts, setAlerts] = useState([]);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [expandedAlert, setExpandedAlert] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [showLiveView, setShowLiveView] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(dayjs());

  // performance KPIs
  const [avgAcknowledgeTime, setAvgAcknowledgeTime] = useState(22);
  const [avgResponseTime, setAvgResponseTime] = useState(148);
  const [acknowledgeTarget] = useState(30);
  const [responseTarget] = useState(120);

  // pulled in from the analytics dashboard
  const [ppeSummary, setPpeSummary] = useState({
    overcrowdingCount: 0,
    loiteringCount: 0,
    intrusionCount: 0,
    rawEvents: [],
  });
  const [personCountTotal, setPersonCountTotal] = useState(0);
  const [personCountTrend, setPersonCountTrend] = useState([]);
  const [receptionStatus, setReceptionStatus] = useState({ reception: null, security: null });

  // ── DATA FETCHING ─────────────────────────────────────────────────
  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const response = await axios.post("/fetch-prestige-alert", {
        page: 1,
        limit: 10,
        camera: "All",
        zoneType: "All",
        date: "",
      });

      if (response.data.success) {
        const formattedAlerts = response.data.alerts
          .filter((item) => item.type !== "overcrowding")
          .slice(0, 6)
          .map((item) => {
          const type = item.type || "unknown";

          const severityMap = {
            intrusion: "critical",
            loitering: "critical",
            altercation: "critical",
            fall: "critical",
            overcrowding: "medium",
            tailgating: "high",
            restricted_entry: "high",
            unattended_object: "medium",
            suspicious: "high",
          };

          const actionMap = {
            fall: "Dispatch medical",
            restricted_entry: "Dispatch patrol",
            unattended_object: "Dispatch patrol",
            intrusion: "Dispatch patrol",
            altercation: "Dispatch patrol",
            loitering: "Dispatch patrol",
            overcrowding: "Monitor crowd",
            tailgating: "Dispatch patrol",
            suspicious: "Dispatch patrol",
          };

          return {
            id: item._id,
            type,
            typeLabel: type.replaceAll("_", " ").replace(/^./, (c) => c.toUpperCase()),
            location: item.camera_id || "Unknown",
            camera: item.camera_id || "Unknown",
            time: getTimeFromIso(item.timestamp),
            severity: severityMap[type],
            status: item.isAcknowledged ? "acknowledged" : "new",
            hasSnapshot: Boolean(item.image_url),
            action: actionMap[type] || "Monitor",
            imageUrl: item.image_url,
            personCount: item.person_count || 0,
          };
        });

        setAlerts(formattedAlerts);
      }
    } catch (error) {
      // Preserve existing behavior without interrupting the dashboard.
    } finally {
      setLoading(false);
    }
  };

  const fetchPpeSummary = async () => {
    try {
      const { data: res } = await axios.post("/fetch-prestigeppkit-dashboard");
      if (res.success) {
        setPpeSummary({
          overcrowdingCount: res.overcrowdingCount || 0,
          loiteringCount: res.loiteringCount || 0,
          intrusionCount: res.intrusionCount || 0,
          rawEvents: res.personCountData || [],
        });
      }
    } catch (error) {
      // non-blocking
    }
  };

  const fetchPersonCount = async () => {
    try {
      const { data: res } = await axios.post("/fetch-prestige-person-count");
      if (res.success) {
        setPersonCountTotal(res.total || 0);
        setPersonCountTrend(
          (res.data || []).map((h) => ({ hour: h.hour, total: h.total || 0 }))
        );
      }
    } catch (error) {
      // non-blocking
    }
  };

  const fetchReceptionStatus = async () => {
    try {
      const { data: res } = await axios.post("/get-reception-status");
      if (res.success) setReceptionStatus(res.data);
    } catch (error) {
      // non-blocking
    }
  };

  const refreshEverything = async () => {
    setRefreshingAll(true);
    await Promise.all([
      fetchAlerts(),
      fetchPpeSummary(),
      fetchPersonCount(),
      fetchReceptionStatus(),
    ]);
    setLastUpdated(dayjs());
    setRefreshingAll(false);
  };

  useEffect(() => {
    refreshEverything();

    const unsubscribe = eventEmitter.on("newAlert", () => {
      fetchAlerts();
      setLastUpdated(dayjs());
    });

    const personInterval = setInterval(fetchPersonCount, 60000);
    const statusInterval = setInterval(fetchReceptionStatus, 30000);

    return () => {
      unsubscribe();
      clearInterval(personInterval);
      clearInterval(statusInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── DERIVED DATA ──────────────────────────────────────────────────
  const counts = useMemo(() => {
    const newCount = alerts.filter((a) => a.status === "new").length;
    const acknowledgedCount = alerts.filter((a) => a.status === "acknowledged").length;
    const dispatchedCount = alerts.filter((a) => a.status === "dispatched").length;
    return { new: newCount, acknowledged: acknowledgedCount, dispatched: dispatchedCount };
  }, [alerts]);

  const openIncidents = useMemo(() => {
    const open = alerts.filter((a) => a.status !== "dispatched");
    const bySeverity = {
      critical: open.filter((a) => a.severity === "critical").length,
      high: open.filter((a) => a.severity === "high").length,
      medium: open.filter((a) => a.severity === "medium").length,
      low: open.filter((a) => a.severity === "low").length,
    };
    return { total: open.length, bySeverity };
  }, [alerts]);

  const topCameras = useMemo(() => {
    const events = ppeSummary.rawEvents || [];
    if (!events.length) return [];
    const counts = {};
    events.forEach((e) => {
      const cam = e.camera_id || "Unknown";
      counts[cam] = (counts[cam] || 0) + 1;
    });
    const max = Math.max(...Object.values(counts), 1);
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([camera, count]) => ({ camera, count, pct: Math.round((count / max) * 100) }));
  }, [ppeSummary.rawEvents]);

  // ── ACTIONS ───────────────────────────────────────────────────────
  const handleAcknowledge = async (alertId) => {
    try {
      await axios.post("/update-is-acknowledged", {
        documentType: "prestige",
        objectId: alertId,
      });

      setAlerts((current) =>
        current.map((a) =>
          a.id === alertId
            ? { ...a, status: "acknowledged" }
            : a
        )
      );

      message.success("Alert acknowledged");
    } catch (error) {
      console.error("Acknowledge alert error:", error);

      message.error(
        error?.response?.data?.message || "Failed to acknowledge alert"
      );
    }
  };

  const handleDispatch = (alertId) => {
    setAlerts((current) =>
      current.map((a) => (a.id === alertId ? { ...a, status: "dispatched" } : a))
    );
    message.success("Patrol dispatched");
  };

  const handleViewLive = (alert) => {
    setSelectedAlert(alert);
    setShowLiveView(true);
  };

  const handleBuildingClick = (building) => {
    message.info(
      `Selected ${building.name} - ${building.people} people, ${building.occupancy}% occupancy`
    );
  };

  const handleZoneClick = (tile) => {
    if (!tile) return;
    message.info(`Selected: ${tile.name || "Unknown"}`);
  };

  const reception = receptionStatus.reception;
  const security = receptionStatus.security;

  return (
    <div className="min-h-screen bg-slate-50/60 p-4 text-slate-900 sm:p-6 lg:p-8">
      <div className="mx-auto flex max-w-[1600px] flex-col gap-5">
        {/* ── HEADER ── */}
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-600 shadow-sm shadow-violet-200">
              <Activity size={18} className="text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-semibold tracking-tight text-slate-900 sm:text-[22px]">
                  Security command center
                </h1>
                <span className="flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[10.5px] font-semibold text-emerald-700">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                  Live
                </span>
              </div>
              <p className="mt-0.5 text-[12.5px] text-slate-500">
                Updated {lastUpdated.fromNow()} · this screen refreshes on its own
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={refreshEverything}
            disabled={refreshingAll}
            className="flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw size={14} className={refreshingAll ? "animate-spin" : ""} />
            {refreshingAll ? "Refreshing…" : "Refresh all"}
          </button>
        </header>

        {/* ── KPI GRID — capped at 4 columns so labels never truncate ── */}
        <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <KpiTile label="Needs attention" value={counts.new} icon={Bell} tone="red" />
          <KpiTile label="Under review" value={counts.acknowledged} icon={Check} tone="amber" />
          <KpiTile label="Intrusions" value={ppeSummary.intrusionCount} icon={ShieldAlert} tone="red" />
          <KpiTile label="Loitering" value={ppeSummary.loiteringCount} icon={ShieldAlert} tone="red" />
          <KpiTile label="Overcrowding events" value={ppeSummary.overcrowdingCount} icon={Users} tone="amber" />
          <KpiTile
            label="Avg acknowledge time"
            value={`${avgAcknowledgeTime}s`}
            icon={Timer}
            tone="blue"
            trend={{ good: avgAcknowledgeTime <= acknowledgeTarget, label: `target ${acknowledgeTarget}s` }}
          />
          <KpiTile
            label="Avg response time"
            value={`${avgResponseTime}s`}
            icon={Gauge}
            tone="teal"
            trend={{ good: avgResponseTime <= responseTarget, label: `target ${responseTarget}s` }}
          />
        </section>

        {/* ── MAIN GRID ── */}
        <section className="grid grid-cols-1 gap-5 lg:grid-cols-12 lg:items-stretch">
          {/* Alerts list */}
          <div className="lg:col-span-3">
            <Panel
              title="Active alerts"
              subtitle="Tap an alert to expand and act"
              icon={Bell}
              right={
                <span className="rounded-full bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-500">
                  {alerts.length}
                </span>
              }
            >
              <div className="divide-y divide-slate-100">
                {loading ? (
                  <div className="py-16 text-center text-sm text-slate-400">Loading alerts…</div>
                ) : alerts.length === 0 ? (
                  <div className="py-16 text-center text-sm text-slate-400">No active alerts</div>
                ) : (
                  alerts.map((alert) => {
                    const style = severityStyle(alert.severity);
                    const SeverityIcon = style.icon;
                    const isExpanded = expandedAlert === alert.id;

                    return (
                      <div key={alert.id} className="transition-colors hover:bg-slate-50/70">
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => setExpandedAlert(isExpanded ? null : alert.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              setExpandedAlert(isExpanded ? null : alert.id);
                            }
                          }}
                          className="cursor-pointer px-5 py-3.5 focus:outline-none focus-visible:bg-slate-50"
                        >
                          <div className="flex items-start gap-3">
                            <div className={`mt-0.5 rounded-lg border p-1.5 ${style.badge}`}>
                              <SeverityIcon size={13} />
                            </div>

                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span className="text-[12.5px] font-semibold text-slate-900">
                                  {alert.typeLabel}
                                </span>
                                <span
                                  className={`rounded-full px-1.5 py-0.5 text-[9.5px] font-semibold capitalize ${
                                    alert.status === "new"
                                      ? "bg-rose-50 text-rose-600"
                                      : alert.status === "acknowledged"
                                        ? "bg-amber-50 text-amber-600"
                                        : "bg-emerald-50 text-emerald-600"
                                  }`}
                                >
                                  {alert.status}
                                </span>
                              </div>
                              <p className="mt-0.5 truncate text-[12px] text-slate-500">
                                {alert.location}
                              </p>
                              <div className="mt-0.5 flex items-center gap-1.5 text-[10.5px] text-slate-400">
                                <span>{alert.camera}</span>
                                <span>·</span>
                                <span>{alert.time}</span>
                              </div>
                            </div>

                            <div className="flex shrink-0 items-center gap-1">
                              {alert.hasSnapshot && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewLive(alert);
                                  }}
                                  className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                                  title="View snapshot"
                                >
                                  <Camera size={13} />
                                </button>
                              )}
                              {isExpanded ? (
                                <ChevronDown size={14} className="text-slate-400" />
                              ) : (
                                <ChevronRight size={14} className="text-slate-400" />
                              )}
                            </div>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="border-t border-slate-100 bg-slate-50/70 px-5 py-3.5">
                            <p className="mb-2.5 text-[12px] font-medium text-slate-600">
                              Recommended action: <span className="font-semibold text-slate-800">{alert.action}</span>
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              <button
                                type="button"
                                onClick={() => handleViewLive(alert)}
                                className="flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-[11.5px] font-medium text-white hover:bg-violet-700"
                              >
                                <Video size={11} />
                                View
                              </button>
                              {alert.status === "new" && (
                                <button
                                  type="button"
                                  onClick={() => handleAcknowledge(alert.id)}
                                  className="flex items-center gap-1.5 rounded-lg bg-sky-600 px-3 py-1.5 text-[11.5px] font-medium text-white hover:bg-sky-700"
                                >
                                  <Check size={11} />
                                  Acknowledge
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </Panel>
          </div>

          {/* Campus live view */}
          <div className="lg:col-span-6">
            <Panel
              title="Campus overview"
              subtitle="Buildings, occupancy and zones"
              right={<span className="hidden text-[10.5px] text-slate-400 sm:inline">Dashed border = restricted</span>}
              bodyClassName="p-3"
            >
              <CampusLiveView onBuildingClick={handleBuildingClick} onZoneClick={handleZoneClick} />
            </Panel>
          </div>

          {/* Right rail */}
          <div className="flex flex-col gap-5 lg:col-span-3">
            <div className="rounded-2xl border border-slate-200/70 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              <div className="flex items-center gap-2.5 border-b border-slate-100 px-5 py-4">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-50 text-slate-500">
                  <Shield size={15} />
                </span>
                <h2 className="text-[13.5px] font-semibold text-slate-900">Front-of-house</h2>
              </div>
              <div className="divide-y divide-slate-100">
                <StatusRow title="Reception" data={reception} Icon={Users} />
                <StatusRow title="Security" data={security} Icon={ShieldAlert} />
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                    <TrendingUp size={15} />
                  </span>
                  <h2 className="text-[13.5px] font-semibold text-slate-900">Occupancy trend</h2>
                </div>
                <span className="text-lg font-semibold text-slate-900">{personCountTotal}</span>
              </div>
              {personCountTrend.length === 0 ? (
                <div className="flex h-24 items-center justify-center text-[11.5px] text-slate-400">
                  No data yet
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={90}>
                  <AreaChart data={personCountTrend}>
                    <defs>
                      <linearGradient id="cdOccGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <Tooltip
                      cursor={false}
                      contentStyle={{ fontSize: 11, borderRadius: 10, border: "1px solid #E2E8F0" }}
                      labelFormatter={() => ""}
                    />
                    <Area
                      type="monotone"
                      dataKey="total"
                      stroke="#10B981"
                      strokeWidth={2}
                      fill="url(#cdOccGrad)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
              <p className="mt-2 text-[11px] text-slate-400">Last 6 hours, all cameras</p>
            </div>

            <div className="flex-1 rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              <div className="mb-3 flex items-center gap-2.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-50 text-slate-500">
                  <HardHat size={15} />
                </span>
                <h2 className="text-[13.5px] font-semibold text-slate-900">Busiest cameras</h2>
              </div>
              {topCameras.length === 0 ? (
                <div className="py-8 text-center text-[11.5px] text-slate-400">No activity yet</div>
              ) : (
                <div className="flex flex-col gap-3">
                  {topCameras.map((c) => (
                    <div key={c.camera}>
                      <div className="mb-1.5 flex items-center justify-between text-[12px]">
                        <span className="font-medium text-slate-700">{c.camera}</span>
                        <span className="font-semibold text-slate-400">{c.count}</span>
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                        <div
                          className="h-full rounded-full bg-violet-500"
                          style={{ width: `${c.pct}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── DETECTION HEATMAP (ported from analytics dashboard) ── */}
        <section>
          <Panel
            title="Detection heatmap"
            subtitle="Camera activity by hour — darker means more detections"
            icon={Thermometer}
            fixedHeight={false}
            bodyClassName="p-5"
          >
            <CameraDetectionHeatmap events={ppeSummary.rawEvents} />
          </Panel>
        </section>

        {/* ── OPEN INCIDENTS STRIP ── */}
        <section className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-200/70 bg-white px-5 py-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-rose-50">
              <AlertOctagon size={16} className="text-rose-500" />
            </div>
            <div>
              <p className="text-[13px] font-semibold text-slate-900">
                {openIncidents.total} open incident{openIncidents.total === 1 ? "" : "s"}
              </p>
              <p className="text-[11.5px] text-slate-400">Unresolved and requiring attention</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 text-[12px] font-medium">
            {["critical", "high", "medium", "low"].map((sev) =>
              openIncidents.bySeverity[sev] > 0 ? (
                <span key={sev} className="flex items-center gap-1.5 capitalize text-slate-600">
                  <span className={`h-2 w-2 rounded-full ${severityStyle(sev).dot}`} />
                  {openIncidents.bySeverity[sev]} {sev}
                </span>
              ) : null
            )}
            {openIncidents.total === 0 && (
              <span className="text-slate-400">All clear — nothing pending</span>
            )}
          </div>
        </section>
      </div>

      {/* ── LIVE VIEW MODAL ── */}
      <Modal
        open={showLiveView}
        onCancel={() => setShowLiveView(false)}
        footer={null}
        title={
          <div className="flex items-center gap-2">
            <Video size={16} className="text-violet-600" />
            <span className="text-sm font-semibold">Live view</span>
            <span className="ml-1 text-xs font-normal text-slate-400">
              {selectedAlert?.camera}
            </span>
          </div>
        }
        width={800}
      >
        {selectedAlert && (
          <div className="space-y-4">
            <div className="flex h-[380px] items-center justify-center rounded-2xl bg-slate-50">
              {selectedAlert.imageUrl && (
                <img
                  src={selectedAlert.imageUrl}
                  alt="Security alert snapshot"
                  className="max-h-[320px] max-w-full rounded-xl object-contain"
                />
              )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-500">
                  Alert: {selectedAlert.typeLabel}
                </span>
                <span
                  className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold capitalize ${severityStyle(
                    selectedAlert.severity
                  ).badge}`}
                >
                  {selectedAlert.severity}
                </span>
              </div>

              <button
                type="button"
                onClick={() => {
                  setShowLiveView(false);
                }}
                className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-slate-800"
              >
                close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CommandDashboard;